import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

import { EquipeInterface } from './equipes';

export declare interface UsuarioInterface {
  id: string;
  foto: string;
  nome: string;
  email: string;
  apelido: string;
  is_access_admin: boolean;
  is_access_chat: boolean;
  is_suporte_interno: boolean;
  disponivel: boolean;
  total_notificacoes_nao_lidas: number;
  avaliacoes: {
    nota_media: number;
    total_avaliacoes: number;
  };
  acessos: {
    is_admin: boolean;
    permissoes: Array<string> | null;
    alias: 'admin' | 'supervisor';
  };
  modulos: {
    is_access_grupo_modulo: boolean;
    is_access_chat_interno_modulo: boolean;
    is_access_envio_em_massa_modulo: boolean;
    can_transcribe_audio: boolean;
  };
  tenant?: {
    id: string;
    nome: string;
  };
}

/**
 * Usuário autenticado: o /v2/auth/me não devolve total_notificacoes_nao_lidas nem os
 * módulos de chat interno e envio em massa.
 */
export declare interface UsuarioLogadoInterface
  extends Omit<
    UsuarioInterface,
    'foto' | 'total_notificacoes_nao_lidas' | 'modulos'
  > {
  foto?: string;
  total_notificacoes_nao_lidas?: number;
  modulos: Partial<UsuarioInterface['modulos']>;
}

export declare interface UsuarioInterfaceV2 {
  id: string;
  name: string;
  email: string;
  foto?: string;
  descricao: string;
  isOnline: boolean;
}

type AtualizarFotoPerfilParamsType = {
  file: string | Blob;
  scaleX: number;
  scaleY: number;
  x: number | undefined;
  y: number | undefined;
  width: number | undefined;
  height: number | undefined;
  aspect: number | undefined;
};

type UsuariosState = {
  minhasEquipes: EquipeInterface[];
  loadingMinhasEquipes: boolean;

  atendentesOnline: UsuarioInterface[];
  loadingAtendentesOnline: boolean;

  allAtendentes: UsuarioInterface[];
  loadingAllAtendentes: boolean;

  selectAtendentes: UsuarioInterfaceV2[];
  loadingSelectAtendentes: boolean;
};

// async thunks

export const fetchMinhasEquipes = createAsyncThunk(
  'usuarios/fetchMinhasEquipes',
  async () => {
    const response = await chatAPI.get('usuarios/minhas_equipes');
    return response.data.data as EquipeInterface[];
  },
);

export const fetchAtendentesOnline = createAsyncThunk(
  'usuarios/fetchAtendentesOnline',
  async () => {
    const response = await chatAPI.get('/usuarios/online');
    return response.data.data as UsuarioInterface[];
  },
);

export const fetchAllUsuarios = createAsyncThunk(
  `usuarios/fetchAllUsuarios`,
  async (equipeId?: string) => {
    const response = await chatAPI.get(`usuarios/all`, {
      params: { equipeId },
    });
    return response.data.data as UsuarioInterface[];
  },
);

export const fetchSelectAtendentes = createAsyncThunk(
  'usuarios/fetchSelectAtendentes',
  async () => {
    const response = await chatAPI.get('v2/users/select', {
      params: {
        type: 'ATENDENTE',
      },
    });

    return response.data as UsuarioInterfaceV2[];
  },
);

export const atualizarFotoPerfil = createAsyncThunk(
  'usuarios/atualizarFotoPerfil',
  async (params: AtualizarFotoPerfilParamsType, { rejectWithValue }) => {
    try {
      const { file, x, y, scaleX, scaleY, width, height, aspect } = params;
      const formData = new FormData();
      formData.append('foto', file);
      formData.append('x', JSON.stringify(x));
      formData.append('y', JSON.stringify(y));
      formData.append('width', JSON.stringify(width));
      formData.append('height', JSON.stringify(height));
      formData.append('aspect', JSON.stringify(aspect));
      formData.append('scaleX', JSON.stringify(scaleX));
      formData.append('scaleY', JSON.stringify(scaleY));

      const response = await chatAPI.post('usuarios/foto-perfil', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data as UsuarioInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const atualizarDisponibilidadeUsuario = createAsyncThunk(
  'usuarios/atualizarDisponibilidadeUsuario',
  async (params: { disponivel: boolean }, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put('v2/users/availability', params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// reducer

const initialState = {
  minhasEquipes: [],
  loadingMinhasEquipes: false,

  atendentesOnline: [],
  loadingAtendentesOnline: false,

  allAtendentes: [],
  loadingAllAtendentes: false,

  selectAtendentes: [],
  loadingSelectAtendentes: false,
} as UsuariosState;

export const usuariosSlice = createSlice({
  name: 'usuarios',
  initialState,
  reducers: {
    resetAllAtendentes: state => {
      state.allAtendentes = [];
    },
    resetMinhasEquipes: state => {
      state.minhasEquipes = [];
    },
  },
  extraReducers(builder) {
    //* Fetch minhas equipes
    builder.addCase(fetchMinhasEquipes.pending, state => {
      state.loadingMinhasEquipes = true;
    });
    builder.addCase(fetchMinhasEquipes.fulfilled, (state, action) => {
      state.minhasEquipes = action.payload;
      state.loadingMinhasEquipes = false;
    });

    //* Fetch atendentes online
    builder.addCase(fetchAtendentesOnline.pending, state => {
      state.loadingAtendentesOnline = true;
    });
    builder.addCase(fetchAtendentesOnline.fulfilled, (state, action) => {
      state.atendentesOnline = action.payload;
      state.loadingAtendentesOnline = false;
    });

    //* Fetch atendentes online
    builder.addCase(fetchAllUsuarios.pending, state => {
      state.loadingAllAtendentes = true;
    });
    builder.addCase(fetchAllUsuarios.fulfilled, (state, action) => {
      state.allAtendentes = action.payload;
      state.loadingAllAtendentes = false;
    });

    // Fetch select de atendentes
    builder.addCase(fetchSelectAtendentes.pending, state => {
      state.loadingSelectAtendentes = true;
    });
    builder.addCase(fetchSelectAtendentes.fulfilled, (state, action) => {
      state.selectAtendentes = action.payload;
      state.loadingSelectAtendentes = false;
    });
  },
});

// actions

export const { resetAllAtendentes, resetMinhasEquipes } = usuariosSlice.actions;

// selectors

export const usuariosSelector = (state: RootState) => state.usuarios;

export default usuariosSlice.reducer;
